import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Icon,
  SearchCategories,
  ShopCardProps,
  SizedBox,
  SlideOverDialog,
  useScreenSizeContext,
  useSlideOverDialogContext,
} from "@bluebottlecoffee/design-system/components";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import {
  Configure,
  InstantSearch,
  SearchBox,
  useHits,
  useSearchBox,
} from "react-instantsearch";
import stripHtml from "striptags";
import { debounce } from "lodash";
import { keyActivate } from "@bluebottlecoffee/design-system/components/utils/keyActivate";
import { getImageSrc } from "../lib/transformers/image";
import { toInternalUrl } from "../lib/transformers/link";
import { pushToDataLayer } from "../lib/analytics";
import { CloudinaryAsset, ShippingCopy } from "../lib/sanity-schema";
import { publicSearchClient } from "../lib/algolia/clients";
import { SearchResults } from "../lib/algolia/instant-search/components/search-results";
import { ConversionCopy } from "../pages/[region]/[lang]/product/[slug]";

export type SearchCategoriesProps = {
  searchCategories: {
    heading: string;
    categories: { text: string; url: string; image: CloudinaryAsset }[];
  };
};

export type SearchDialogCopy = {
  productSearchCopy: {
    inputPlaceholder: string;
    searchResultsCopy: string;
    searchResultsCopyDefault: string;
    searchResultsNotFoundCopy: string;
    searchResultsNotFoundSuggestionCopy: string;
    closeButton: string;
  };
  shopcardCopy: {
    shippingCopy: ShippingCopy;
    conversionCopy: ConversionCopy;
  };
};

type SearchDialogProps = SearchDialogCopy &
  SearchCategoriesProps & {
    lang: string;
    region: string;
    noResultsShopCards: ShopCardProps[];
  };

const searchIndexName = `${process.env.NEXT_PUBLIC_SANITY_STUDIO_API_DATASET}-products`;

export const SearchDialogContent: FunctionComponent<
  Omit<SearchDialogProps, "searchCategories">
> = ({ lang, region, productSearchCopy, shopcardCopy, noResultsShopCards }) => {
  const {
    results: { query },
  } = useHits();
  const { refine } = useSearchBox();
  const { activeContentType } = useSlideOverDialogContext();
  const { asPath } = useRouter();

  useEffect(() => {
    if (activeContentType !== "search") return;

    const getSearchDialogText = Cookies.get("searchDialog");
    const backToSearchLogic = asPath.includes("search=true");

    if (getSearchDialogText && backToSearchLogic) {
      refine(getSearchDialogText);
    }
  }, [activeContentType]);

  return (
    <>
      <SearchResults
        lang={lang}
        region={region}
        searchIndexName={searchIndexName}
        productSearchCopy={productSearchCopy}
        shopcardCopy={shopcardCopy}
        noResultsShopCards={noResultsShopCards}
      />
      <Configure
        // @ts-ignore
        clickAnalytics
        hitsPerPage={query ? 6 : 30} // TODO: determine hit limit? pagination?
        distinct
        filters="forSale:true"
      />
    </>
  );
};

export const SearchDialog: FunctionComponent<SearchDialogProps> = ({
  productSearchCopy,
  searchCategories,
  ...props
}) => {
  const searchIconBuilder = useCallback(
    () => <Icon hoverOpacity={false} name="search" />,
    [],
  );

  const [renderSearchBox, setRenderSearchBox] = useState(false);
  const { isMobile } = useScreenSizeContext();
  const { activeContentType, setActiveContentType } =
    useSlideOverDialogContext();
  const router = useRouter();

  useEffect(() => {
    if (activeContentType === "search") {
      // analytics tracking for search dialog open
      pushToDataLayer({
        event: "search_click",
      });

      // SlideOverDialog makes the dismissButton the focus; with this timeout, we force the SearchBox to be the focus.
      // https://github.com/bluebottlecoffee/design-system/blob/main/components/SlideOverDialog/SlideOverDialog.tsx#L137
      setTimeout(() => {
        setRenderSearchBox(true);
      }, 50);
    }

    return () => {
      setRenderSearchBox(false);
    };
  }, [activeContentType]);

  // `currentQuery` must survive renders to actually de-duplicate. As a plain
  // `let` it was re-initialised to null on every render, so the equality
  // check below never matched and every keystroke re-reported.
  const currentQuery = useRef<string | null>(null);

  // `debounce` must be created once. Re-creating it per render produced a
  // fresh timer each time (so nothing was ever really debounced) and, more
  // importantly, gave `queryHook` a new identity on every render -- see below.
  const debounceTrackSearchQuery = useMemo(
    () =>
      debounce((query: string) => {
        const searchQuery = query.trim();
        if (!searchQuery || searchQuery === currentQuery.current) return;
        currentQuery.current = searchQuery;

        pushToDataLayer({
          event: "search",
          search_term: searchQuery,
        });
      }, 500),
    [],
  );

  // Cancel any in-flight debounced report on unmount so it cannot fire
  // against a torn-down tree.
  useEffect(
    () => () => debounceTrackSearchQuery.cancel(),
    [debounceTrackSearchQuery],
  );

  // Stable identities for every prop handed to <SearchBox/>. react-instantsearch
  // keys its SearchBox connector off these; when they changed on each render the
  // widget was re-registered, and re-registration resets the index query to "".
  // That is what made search results appear correctly and then vanish ~1.4s
  // later, replaced by the empty-query ("Most Popular") state -- a late search
  // with an empty query landed last and clobbered the real results.
  const queryHook = useCallback(
    (query: string, search: (value: string) => void) => {
      search(stripHtml(query));
      Cookies.set("searchDialog", query);
      debounceTrackSearchQuery(query);
    },
    [debounceTrackSearchQuery],
  );
  const renderNull = useCallback(() => null, []);

  // Clear search dialog state on route change
  useEffect(() => {
    const handleRouteChange = () => setActiveContentType(null);

    router.events.on("routeChangeStart", handleRouteChange);
    return () => router.events.off("routeChangeStart", handleRouteChange);
  }, [router.events, setActiveContentType]);

  return (
    <InstantSearch
      indexName={searchIndexName}
      searchClient={publicSearchClient}
      insights
    >
      <SlideOverDialog
        contentType="search"
        dialogBackgroundColor="white"
        dismissText="close"
        className="search-dialog-slideOverDialog"
        title="Search Dialog"
        isFullWidth
        showDismissButton={false}
      >
        <div className="search-dialog-header">
          {!isMobile() && <Icon name="blueBottleLogo" ariaHidden />}

          {renderSearchBox && (
            <SearchBox
              classNames={{
                root: "search-dialog-input-container-test search-dialog-input-container",
                input: "search-dialog-input",
                submit: "search-dialog-input-submit",
              }}
              placeholder={productSearchCopy.inputPlaceholder}
              loadingIconComponent={renderNull} // Algolia default renders this and it's weird
              resetIconComponent={renderNull} // Algolia default renders an extra?!
              submitIconComponent={searchIconBuilder}
              queryHook={queryHook}
              autoFocus
            />
          )}

          <Icon
            name="x"
            onClick={() => setActiveContentType(null)}
            onKeyDown={(event) =>
              keyActivate(event, () => setActiveContentType(null))
            }
            title={productSearchCopy.closeButton}
            className="cursor-pointer search-dialog-close-button"
          />
        </div>

        <SearchCategories
          heading={searchCategories.heading}
          categories={searchCategories.categories.map((category) => ({
            text: category.text,
            url: toInternalUrl({
              lang: props.lang,
              region: props.region,
              link: category.url,
            }),
            imageSrc: getImageSrc(category.image),
          }))}
        />
        <SizedBox height={16} />
        <SearchDialogContent productSearchCopy={productSearchCopy} {...props} />
      </SlideOverDialog>
    </InstantSearch>
  );
};
